import { Dispatch, FC, Fragment, SetStateAction, useState } from "react";
import { Box, Chip, Divider, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import UserInformation from "../../types/UserInformation";
import Cookies from "universal-cookie";
import CheckAttr from "../../utils/CheckAttr";
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from "@mui/lab";
import { AlertType } from "../../types/AlertType";
import { SnackBarAlert } from "../element/SnackBarAlert";
import { useDispatch } from "react-redux";
import { loggingUser, loggoutUser } from "../../redux/actions/logginUser";
import axios, { AxiosInstance } from "axios";
import { DataRef } from "../../types/DataRef";
import { useNavigate } from 'react-router-dom';


interface AccountFormProps {
    user: UserInformation;
    dataPays: DataRef[];
}

interface EditUser {
    id?: number;
    name: string;
    prenom: string;
    email: string;
    pays?: number[];
    Departement?: string;
    /** Affichage seul : le profil n'est pas modifiable depuis "Mon compte" et n'est jamais envoyé à l'API */
    profil?: string;
}

interface ValidationInput {
    name: boolean;
    prenom: boolean;
    email: boolean;
    pays: boolean;
}

const resetValidation: ValidationInput = {
    name: false,
    prenom: false,
    email: false,
    pays: false
}

const urlBaseApi = process.env.REACT_APP_BASE_API_URL as string;

const AccountForm: FC<AccountFormProps> = ({ user, dataPays }) => {
    const currentUser: EditUser = {
        id: user?.id,
        name: user?.name,
        prenom: user?.prenom,
        email: user?.email,
        pays: user?.pays?.map(pays => pays?.id),
        Departement: user?.Departement,
        profil: user?.profils?.libelle
    }

    const dispatch = useDispatch();

    const [isClickSave, setIsClickSave] = useState<boolean>(false);
    const [openSnack, setOpenSnack] = useState<boolean>(false);
    const [openSelectMultiple, setOpenSelectMultiple] = useState<boolean>(false);

    const [alertMessage, setAlerteMessage] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertType>("success");

    const [errorInput, setErrorInput] = useState<ValidationInput>(resetValidation);
    const [userToSave, setUserToSave] = useState<EditUser>(currentUser);

    const cookies = new Cookies();
    const token: string = cookies.get("jwt");

    const http: AxiosInstance = axios.create({
        baseURL: urlBaseApi,
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    const navigate = useNavigate();


   // Add global response interceptor
    http.interceptors.response.use(
        (response) => {
        // Modify response data here, if needed
        return response;
        },
        (error) => {
            console.log(error.message);
            if(error.message ==="Request failed with status code 401"){
                // Remove the redux store
                dispatch(loggoutUser());

                // Remove token in navigator cookies
                cookies.remove('jwt', { path: '/' });

                navigate("/sign-in");
            }
            return Promise.reject(error);
        }
    );

    const handleChange = (e: any) => {
        const { name, value } = e.target;

        setUserToSave({
            ...userToSave,
            [name]: value
        })
    };

    const isValidate = (): boolean => {
        // Reinitialise les variables des validations
        setErrorInput(resetValidation);

        if (userToSave?.name?.length === 0 ||
            userToSave?.prenom?.length === 0 ||
            userToSave?.email?.length === 0 ||
            !CheckAttr(userToSave?.pays)) {
            if (userToSave?.name?.length === 0) {
                setErrorInput((prev: ValidationInput) => ({ ...prev, name: true }));
            }

            if (userToSave?.prenom?.length === 0) {
                setErrorInput((prev: ValidationInput) => ({ ...prev, prenom: true }));
            }

            if (userToSave?.email?.length === 0) {
                setErrorInput((prev: ValidationInput) => ({ ...prev, email: true }));
            }

            if (!CheckAttr(userToSave?.pays)) {
                setErrorInput((prev: ValidationInput) => ({ ...prev, pays: true }));
            }

            setAlerteMessage("Merci de remplir les champs en rouge !");
            setAlertType("warning");
            setOpenSnack(true);

            return false;
        }

        return true;
    }

    const handleSave = () => {
        if (isValidate()) {
            setIsClickSave(true);

            // Le profil est affiché à titre informatif : on ne l'envoie pas, l'API conserve alors les profils existants
            const { profil, ...userSansProfil } = userToSave;

            http.put(`user/edit/${userToSave?.id}`, userSansProfil)
                .then(res => {
                    const { name, prenom, Departement, pays } = res.data.user;
                    const updateUser: UserInformation = {
                        ...user,
                        name,
                        prenom,
                        Departement,
                        pays
                    }

                    dispatch(loggingUser(updateUser));

                    setAlerteMessage("Enregistrement efféctuée !");
                    setAlertType("success");
                    setOpenSnack(true);
                }).catch(error => {
                    console.log(error);
                    setAlertType("error");
                    setOpenSnack(true);
                }).finally(() => {
                    setIsClickSave(false);
                });
        }
    };

    const handleCloseSelectMulti = () => {
        setOpenSelectMultiple((prev) => !prev);
    };

    const handleChangeSelectMultitple = (e: any) => {
        e.stopPropagation();
        handleCloseSelectMulti();
        setUserToSave((prev) => ({
            ...prev,
            pays: e.target.value,
        }));
    };

    const handleCloseAlert = () => {
        setOpenSnack(false);
    }

    const stackAlert: JSX.Element = SnackBarAlert(openSnack, handleCloseAlert, alertType, alertMessage);

    return (
        <Fragment>
            <Typography variant="h6" gutterBottom color="#FC8404">
                Paramètrage du profil
                <Divider />
            </Typography>
            <Grid container spacing={2} mt={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        name="name"
                        label="Nom"
                        color="warning"
                        error={errorInput.name}
                        fullWidth
                        autoComplete="off"
                        variant="standard"
                        value={userToSave?.name}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: CheckAttr(userToSave?.name) }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        name="prenom"
                        label="Prénom"
                        color="warning"
                        error={errorInput.prenom}
                        fullWidth
                        autoComplete="off"
                        variant="standard"
                        value={userToSave?.prenom}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: CheckAttr(userToSave?.prenom) }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        required
                        name="email"
                        label="Email"
                        fullWidth
                        color="warning"
                        autoComplete="off"
                        variant="standard"
                        value={userToSave?.email}
                        error={errorInput.email}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: CheckAttr(userToSave?.email) }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl variant="standard" fullWidth>
                        <InputLabel htmlFor="profil-contact" color="warning" shrink={true}>
                            Pays
                        </InputLabel>
                        <Select
                            open={openSelectMultiple}
                            onClick={() => handleCloseSelectMulti()}
                            multiple
                            error={errorInput.pays}
                            displayEmpty
                            value={userToSave?.pays}
                            size="small"
                            onChange={handleChangeSelectMultitple}
                            renderValue={(selected) => {
                                return (
                                    <div>
                                        {((selected as number[]) || []).map((value) => (
                                            <Chip
                                                key={value}
                                                label={dataPays.find(item => item.id === value)?.libelle}
                                                size="small"
                                            />
                                        ))}
                                    </div>
                                );
                            }}
                            label="Pays"
                            color="warning"
                            inputProps={{
                                name: 'pays',
                                id: 'pays',
                            }}
                            MenuProps={{
                                sx: {
                                    "&& .Mui-selected": {
                                        backgroundColor: "#080d24",
                                        color: "#fff"
                                    }
                                }
                            }}
                        >
                            <MenuItem disabled value="" dense></MenuItem>
                            {dataPays.map(pays => (
                                <MenuItem
                                    key={pays.id}
                                    value={pays.id}
                                    onClick={() => handleCloseSelectMulti()}
                                    dense
                                >
                                    {pays.libelle}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        name="Departement"
                        label="Département"
                        color="warning"
                        fullWidth
                        autoComplete="off"
                        variant="standard"
                        value={userToSave?.Departement}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: CheckAttr(userToSave?.Departement) }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        disabled
                        label="Profil"
                        color="warning"
                        fullWidth
                        variant="standard"
                        value={userToSave?.profil}
                        InputLabelProps={{ shrink: CheckAttr(userToSave?.profil) }}
                    />
                </Grid>
            </Grid>
            <Box display="flex" justifyContent="center" mt={3}>
                <LoadingButton
                    onClick={() => handleSave()}
                    endIcon={<SaveIcon />}
                    loading={isClickSave}
                    loadingPosition="end"
                    sx={{ textTransform: "none" }}
                    variant="contained"
                    color="warning"
                >
                    Enregistrer
                </LoadingButton>
            </Box>
            {stackAlert}
        </Fragment>
    );
};

export default AccountForm;